//src/hooks/useRiskInventories.ts
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  createInventoryItemActionPlanItem,
  deleteInventoryItemActionPlanItem,
  generateRiskInventoryAiInsights,
  createRiskInventory,
  createRiskInventoryItem,
  deleteRiskInventoryItem,
  getInventoryItemActionPlan,
  listInventoryActionPlanResponsibleUsers,
  getRiskInventory,
  getRiskInventoryItemFormOptions,
  linkActionPlanModelToInventoryItem,
  listInventoryItemActionPlans,
  listRiskInventoryAiInsights,
  listRiskInventories,
  listRiskInventoryItemLogs,
  listRiskInventoryItems,
  listSuggestedActionPlanModels,
  removeRiskInventoryItemFromInventory,
  searchActionPlanModels,
  setInventoryItemActionPlanItemStatus,
  setRiskInventoryItemStatus,
  setRiskInventoryStatus,
  unlinkInventoryItemActionPlan,
  updateInventoryItemActionPlanItem,
  updateRiskInventory,
  updateRiskInventoryAiInsightFeedback,
  updateRiskInventoryItem,
} from "@/services/riskInventories";
import type {
  OperationalPlanItemStatus,
  RiskInventoryCreateIn,
  RiskInventoryItemCreateIn,
  RiskInventoryItemUpdateIn,
  RiskInventoryLinkActionPlanIn,
  RiskInventoryOperationalActionPlanItemCreateIn,
  RiskInventoryOperationalActionPlanItemUpdateIn,
  RiskInventoryStatus,
  RiskInventoryUpdateIn,
} from "@/types/riskInventory";

export const RISK_INVENTORIES_QK = (params: { status?: string; unit_id?: string }) => ["risk_inventories", params] as const;

export function useRiskInventories(params: { status?: string; unit_id?: string }) {
  return useQuery({ queryKey: RISK_INVENTORIES_QK(params), queryFn: () => listRiskInventories(params), staleTime: 20_000 });
}
export function useRiskInventoryDetail(id: string | undefined) {
  return useQuery({ queryKey: ["risk_inventory_detail", id], queryFn: () => getRiskInventory(id as string), enabled: !!id, staleTime: 20_000 });
}
export function useRiskInventoryItems(inventoryId: string | undefined, params: { status?: string; dimension_id?: string; page?: number; per_page?: number }) {
  return useQuery({
    queryKey: ["risk_inventory_items", inventoryId, params],
    queryFn: () => listRiskInventoryItems(inventoryId as string, params),
    enabled: !!inventoryId,
    staleTime: 20_000,
  });
}
export function useRiskInventoryItemFormOptions(inventoryId: string | undefined) {
  return useQuery({ queryKey: ["risk_inventory_item_form_options", inventoryId], queryFn: () => getRiskInventoryItemFormOptions(inventoryId as string), enabled: !!inventoryId, staleTime: 20_000 });
}
export function useRiskInventoryItemLogs(itemId: string | undefined, enabled = true) {
  return useQuery({ queryKey: ["risk_inventory_item_logs", itemId], queryFn: () => listRiskInventoryItemLogs(itemId as string), enabled: !!itemId && enabled, staleTime: 5_000 });
}

export function useSuggestedActionPlanModels(itemId: string | undefined, enabled = true) {
  return useQuery({
    queryKey: ["inventory_item_action_plan_suggested_models", itemId],
    queryFn: () => listSuggestedActionPlanModels(itemId as string),
    enabled: !!itemId && enabled,
    staleTime: 20_000,
  });
}

export function useSearchActionPlanModels(itemId: string | undefined, q: string, enabled = true) {
  return useQuery({
    queryKey: ["inventory_item_action_plan_search_models", itemId, q],
    queryFn: () => searchActionPlanModels(itemId as string, q),
    enabled: !!itemId && enabled,
    staleTime: 20_000,
  });
}

export function useInventoryItemActionPlans(itemId: string | undefined, enabled = true) {
  return useQuery({
    queryKey: ["inventory_item_action_plans", itemId],
    queryFn: () => listInventoryItemActionPlans(itemId as string),
    enabled: !!itemId && enabled,
    staleTime: 10_000,
  });
}

export function useInventoryItemActionPlanDetail(itemId: string | undefined, planId: string | undefined, enabled = true) {
  return useQuery({
    queryKey: ["inventory_item_action_plan_detail", itemId, planId],
    queryFn: () => getInventoryItemActionPlan(itemId as string, planId as string),
    enabled: !!itemId && !!planId && enabled,
    staleTime: 10_000,
  });
}

export function useInventoryActionPlanResponsibleUsers(inventoryId: string | undefined, enabled = true) {
  return useQuery({
    queryKey: ["inventory_action_plan_responsible_users", inventoryId],
    queryFn: () => listInventoryActionPlanResponsibleUsers(inventoryId as string),
    enabled: !!inventoryId && enabled,
    staleTime: 60_000,
  });
}

function invalidate(qc:any, inventoryId?: string, itemId?: string){
  qc.invalidateQueries({ queryKey: ["risk_inventories"] });
  if(inventoryId){
    qc.invalidateQueries({ queryKey: ["risk_inventory_detail", inventoryId] });
    qc.invalidateQueries({ queryKey: ["risk_inventory_items", inventoryId] });
  }
  if(itemId) qc.invalidateQueries({ queryKey: ["risk_inventory_item_logs", itemId] });
}

function invalidateActionPlans(qc: any, itemId?: string, planId?: string) {
  if (itemId) {
    qc.invalidateQueries({ queryKey: ["inventory_item_action_plan_suggested_models", itemId] });
    qc.invalidateQueries({ queryKey: ["inventory_item_action_plan_search_models", itemId] });
    qc.invalidateQueries({ queryKey: ["inventory_item_action_plans", itemId] });
  }
  if (itemId && planId) {
    qc.invalidateQueries({ queryKey: ["inventory_item_action_plan_detail", itemId, planId] });
  }
}

export function useCreateRiskInventory(){ const qc=useQueryClient(); return useMutation({ mutationFn:(body:RiskInventoryCreateIn)=>createRiskInventory(body), onSuccess:()=>invalidate(qc) });}
export function useUpdateRiskInventory(){ const qc=useQueryClient(); return useMutation({ mutationFn:({inventoryId,body}:{inventoryId:string;body:RiskInventoryUpdateIn;})=>updateRiskInventory(inventoryId,body), onSuccess:(_d,v)=>invalidate(qc,v.inventoryId) });}
export function useSetRiskInventoryStatus(){ const qc=useQueryClient(); return useMutation({ mutationFn:({inventoryId,status}:{inventoryId:string;status:RiskInventoryStatus;})=>setRiskInventoryStatus(inventoryId,{status}), onSuccess:(_d,v)=>invalidate(qc,v.inventoryId) });}
export function useCreateRiskInventoryItem(){ const qc=useQueryClient(); return useMutation({ mutationFn:({inventoryId,body}:{inventoryId:string;body:RiskInventoryItemCreateIn;})=>createRiskInventoryItem(inventoryId,body), onSuccess:(_d,v)=>invalidate(qc,v.inventoryId) });}
export function useUpdateRiskInventoryItem(){ const qc=useQueryClient(); return useMutation({ mutationFn:({itemId,inventoryId,body}:{itemId:string;inventoryId:string;body:RiskInventoryItemUpdateIn;})=>updateRiskInventoryItem(itemId,body), onSuccess:(_d,v)=>invalidate(qc,v.inventoryId,v.itemId) });}
export function useSetRiskInventoryItemStatus(){ const qc=useQueryClient(); return useMutation({ mutationFn:({itemId,inventoryId,status,technicalOpinion}:{itemId:string;inventoryId:string;status:"open"|"mitigated"|"closed";technicalOpinion:string;})=>setRiskInventoryItemStatus(itemId,{status,technical_opinion:technicalOpinion}), onSuccess:(_d,v)=>invalidate(qc,v.inventoryId,v.itemId) });}
export function useDeleteRiskInventoryItem(){ const qc=useQueryClient(); return useMutation({ mutationFn:({itemId,inventoryId}:{itemId:string;inventoryId:string;})=>deleteRiskInventoryItem(itemId), onSuccess:(_d,v)=>invalidate(qc,v.inventoryId,v.itemId) });}
export function useRemoveRiskInventoryItemFromInventory(){ const qc=useQueryClient(); return useMutation({ mutationFn:({itemId,inventoryId}:{itemId:string;inventoryId:string;})=>removeRiskInventoryItemFromInventory(itemId), onSuccess:(_d,v)=>{invalidate(qc,v.inventoryId,v.itemId); qc.invalidateQueries({queryKey:["survey_risk_suggestions_queue"]}); qc.invalidateQueries({queryKey:["survey_risk_suggestions_summary"]});} });}

export function useLinkActionPlanModelToInventoryItem() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ itemId, body }: { itemId: string; body: RiskInventoryLinkActionPlanIn }) => linkActionPlanModelToInventoryItem(itemId, body),
    onSuccess: (_data, variables) => invalidateActionPlans(qc, variables.itemId),
  });
}

export function useUnlinkInventoryItemActionPlan() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ itemId, planId }: { itemId: string; planId: string }) => unlinkInventoryItemActionPlan(itemId, planId),
    onSuccess: (_data, variables) => invalidateActionPlans(qc, variables.itemId, variables.planId),
  });
}

export function useCreateInventoryItemActionPlanItem() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ itemId, planId, body }: { itemId: string; planId: string; body: RiskInventoryOperationalActionPlanItemCreateIn }) =>
      createInventoryItemActionPlanItem(itemId, planId, body),
    onSuccess: (_data, variables) => invalidateActionPlans(qc, variables.itemId, variables.planId),
  });
}

export function useUpdateInventoryItemActionPlanItem() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ itemId, planId, planItemId, body }: { itemId: string; planId: string; planItemId: string; body: RiskInventoryOperationalActionPlanItemUpdateIn }) =>
      updateInventoryItemActionPlanItem(itemId, planId, planItemId, body),
    onSuccess: (_data, variables) => invalidateActionPlans(qc, variables.itemId, variables.planId),
  });
}

export function useSetInventoryItemActionPlanItemStatus() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ itemId, planId, planItemId, status }: { itemId: string; planId: string; planItemId: string; status: OperationalPlanItemStatus }) =>
      setInventoryItemActionPlanItemStatus(itemId, planId, planItemId, status),
    onSuccess: (_data, variables) => invalidateActionPlans(qc, variables.itemId, variables.planId),
  });
}

export function useDeleteInventoryItemActionPlanItem() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ itemId, planId, planItemId }: { itemId: string; planId: string; planItemId: string }) =>
      deleteInventoryItemActionPlanItem(itemId, planId, planItemId),
    onSuccess: (_data, variables) => invalidateActionPlans(qc, variables.itemId, variables.planId),
  });
}


export function useRiskInventoryAiInsights(inventoryId: string | undefined, params: { page?: number; per_page?: number }) {
  return useQuery({
    queryKey: ["risk_inventory_ai_insights", inventoryId, params],
    queryFn: () => listRiskInventoryAiInsights(inventoryId as string, params),
    enabled: !!inventoryId,
    staleTime: 15_000,
  });
}

export function useGenerateRiskInventoryAiInsights() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ inventoryId }: { inventoryId: string }) => generateRiskInventoryAiInsights(inventoryId),
    onSuccess: (_data, variables) => {
      qc.invalidateQueries({ queryKey: ["risk_inventory_ai_insights", variables.inventoryId] });
      qc.invalidateQueries({ queryKey: ["ai_wallet"] });
      qc.invalidateQueries({ queryKey: ["ai_history"] });
    },
  });
}


export function useUpdateRiskInventoryAiInsightFeedback() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ inventoryId, outputId, feedbackStatus }: { inventoryId: string; outputId: string; feedbackStatus: "like" | "unlike" | null }) =>
      updateRiskInventoryAiInsightFeedback(inventoryId, outputId, feedbackStatus),
    onSuccess: (_data, variables) => {
      qc.invalidateQueries({ queryKey: ["risk_inventory_ai_insights", variables.inventoryId] });
      qc.invalidateQueries({ queryKey: ["ai_history"] });
    },
  });
}
